import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  Eye, EyeOff, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownLeft,
  Send, CreditCard, FileText, Receipt, ShieldCheck, Sparkles, AlertCircle,
  ChevronRight, Globe, ArrowRight, PiggyBank, Building2, Award,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import AccountStatusBanner, { getStatusConfig } from '../../components/AccountStatusBanner';
import { useLanguage } from '../../lib/LanguageContext';

const tierStyle = {
  standard: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Standard', glow: '' },
  premium: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Premium', glow: 'shadow-blue-500/20' },
  gold: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Gold', glow: 'shadow-amber-500/20' },
  diamond: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Diamond', glow: 'shadow-purple-500/20' },
};

export default function Accueil() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [recentTx, setRecentTx] = useState([]);
  const [hideBalance, setHideBalance] = useState(false);

  useEffect(() => { (async () => {
    try {
      const r = await api.get('/client/me');
      setData(r.data);
    } catch { toast.error(t('common.loadError')); }
    try {
      const s = await api.get('/public/stocks');
      setStocks(s.data.quotes || []);
    } catch { /* silent */ }
    try {
      const tx = await api.get('/client/transactions', { params: { limit: 5 } });
      setRecentTx(tx.data || []);
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })(); }, []);

  if (!data) return <div className="text-slate-500">{t('common.loading')}</div>;
  const u = data.user;
  const s = tierStyle[u.statut_client || 'standard'];
  const cfg = getStatusConfig(u.statut_compte || 'actif');
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 6) return t('dashboard.goodEvening');
    if (h < 12) return t('dashboard.goodMorning');
    if (h < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">{greeting}, {u.prenom} 👋</h1>
          <p className="text-slate-500 text-sm mt-0.5">{t('dashboard.welcomeSpace')}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-white border rounded-full px-3 py-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> {t('dashboard.secureConnection')}
        </div>
      </div>

      {/* Status banner */}
      <AccountStatusBanner statut={u.statut_compte || 'actif'} />

      {/* Solde card pro */}
      <div className={`rounded-3xl p-6 md:p-8 text-white relative overflow-hidden shadow-xl ${s.glow}`} style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%)' }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-indigo-500/20 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-center gap-2 text-blue-200 text-sm">
            <Wallet className="w-4 h-4"/> {t('dashboard.mainBalance')}
            <Badge className={`ml-2 ${s.bg} ${s.text} font-semibold`}>{s.label}</Badge>
          </div>
          <div className="mt-4 flex items-baseline gap-3 flex-wrap">
            <div className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="balance-display">
              {hideBalance ? '••••• €' : `${(u.solde || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
            </div>
            <button onClick={() => setHideBalance(!hideBalance)} className="inline-flex items-center gap-1 text-xs text-blue-200 hover:text-white" data-testid="btn-toggle-balance">
              {hideBalance ? <><Eye className="w-3.5 h-3.5"/> {t('dashboard.show')}</> : <><EyeOff className="w-3.5 h-3.5"/> {t('dashboard.hide')}</>}
            </button>
          </div>
          <div className="mt-4 text-xs text-blue-200">
            {t('dashboard.holder')} : <span className="text-white font-medium">{u.civilite} {u.prenom} {u.nom}</span>
          </div>
          {/* Quick stats inline */}
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-xl">
            <Mini label={t('dashboard.thisMonth')} value={data.stats?.transactions_mois || 0} suffix=" tx" sub={t('dashboard.operations')}/>
            <Mini label={t('dashboard.income')} value={(data.stats?.revenus_mois || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} suffix=" €" trend="up"/>
            <Mini label={t('dashboard.expenses')} value={Math.abs(data.stats?.depenses_mois || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} suffix=" €" trend="down"/>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickAction to="/dashboard/virement" icon={Send} label={t('dashboard.newTransfer')} disabled={!cfg.canTransfer} accent="blue"/>
        <QuickAction to="/dashboard/carte" icon={CreditCard} label={t('dashboard.myCards')} accent="indigo"/>
        <QuickAction to="/dashboard/rib" icon={FileText} label={t('dashboard.myRib')} accent="emerald"/>
        <QuickAction to="/dashboard/transactions" icon={Receipt} label={t('dashboard.transactionsLabel')} accent="purple"/>
      </div>

      {/* 2-col grid : Activité récente + Services */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Activité récente (2 cols) */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-700"/> {t('dashboard.recentActivity')}
            </h2>
            <Link to="/dashboard/transactions" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1">
              {t('dashboard.viewAll')} <ChevronRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentTx.length === 0 && <div className="py-6 text-center text-slate-400 text-sm">{t('dashboard.noRecentTransactions')}</div>}
            {recentTx.slice(0, 5).map((tx) => (
              <div key={tx.id} className="py-3 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${tx.montant >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {tx.montant >= 0 ? <ArrowDownLeft className="w-4 h-4"/> : <ArrowUpRight className="w-4 h-4"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{tx.libelle}</div>
                  <div className="text-xs text-slate-500">{new Date(tx.date_transaction).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                </div>
                <div className={`font-semibold text-sm ${tx.montant >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {tx.montant >= 0 ? '+' : ''}{tx.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Services & alertes (1 col) */}
        <div className="space-y-4">
          {/* Premium upsell ou Tier card */}
          <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-500/20">
            <Award className="w-6 h-6"/>
            <div className="mt-3 text-xs uppercase tracking-widest opacity-90">{t('dashboard.program')}</div>
            <div className="text-xl font-bold">{t('dashboard.tierStatus', { tier: s.label })}</div>
            <p className="text-sm text-white/90 mt-2">{t('dashboard.tierDesc')}</p>
          </div>

          {/* Services bancaires */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-sm">{t('dashboard.bankServices')}</h3>
            <div className="mt-3 space-y-2">
              <ServiceLink to="/dashboard/rib" icon={FileText} label={t('dashboard.downloadMyRib')} desc={t('dashboard.ribDesc')}/>
              <ServiceLink to="/dashboard/profil" icon={Building2} label={t('dashboard.myInfo')} desc={t('dashboard.myInfoDesc')}/>
              <ServiceLink to="/dashboard/virement/historique" icon={Globe} label={t('dashboard.transferHistory')} desc={t('dashboard.transferHistoryDesc')}/>
            </div>
          </div>

          {/* Conseil sécurité */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-700 shrink-0 mt-0.5"/>
              <div>
                <div className="font-semibold text-sm text-blue-900">{t('dashboard.securityTip')}</div>
                <p className="text-xs text-blue-800 mt-1">{t('dashboard.securityTipDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats détaillées */}
      <div className="grid md:grid-cols-3 gap-4">
        <StatCard label={t('dashboard.monthlyTransactions')} value={data.stats?.transactions_mois || 0} icon={Receipt} accent="blue"/>
        <StatCard label={t('dashboard.monthlyExpensesLabel')} value={`${Math.abs(data.stats?.depenses_mois || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`} icon={TrendingDown} accent="rose"/>
        <StatCard label={t('dashboard.monthlyIncomeLabel')} value={`${(data.stats?.revenus_mois || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`} icon={TrendingUp} accent="emerald"/>
      </div>

      {/* Bourse */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-700"/> {t('dashboard.financialMarkets')}</h2>
          <Link to="/bourse" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1">{t('dashboard.learnMore')} <ChevronRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {stocks.length === 0 && <div className="text-slate-400 text-sm col-span-full text-center py-4">{t('dashboard.loadingIndices')}</div>}
          {stocks.map((s) => (
            <div key={s.symbol} className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-300 transition">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-medium truncate">{s.name}</div>
              {s.error ? <div className="text-rose-500 text-xs mt-2">{t('dashboard.unavailable')}</div> : <>
                <div className="text-lg font-bold mt-1">{s.price?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                <div className={`text-xs font-semibold flex items-center gap-1 ${s.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {s.change >= 0 ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownLeft className="w-3 h-3"/>}
                  {s.change >= 0 ? '+' : ''}{s.change_pct?.toFixed(2)}%
                </div>
              </>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, disabled, accent = 'blue' }) {
  const accents = {
    blue: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
    indigo: 'from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800',
    emerald: 'from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800',
    purple: 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
  };
  const className = `flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br ${accents[accent]} text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5`;
  if (disabled) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200" data-testid={`quick-${label}-disabled`}>
        <Icon className="w-5 h-5"/>
        <div className="font-medium text-sm">{label}</div>
        <AlertCircle className="w-4 h-4 ml-auto"/>
      </div>
    );
  }
  return (
    <Link to={to} className={className} data-testid={`quick-${label}`}>
      <Icon className="w-5 h-5"/>
      <div className="font-medium text-sm">{label}</div>
      <ArrowRight className="w-4 h-4 ml-auto opacity-70"/>
    </Link>
  );
}

function ServiceLink({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition group">
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center group-hover:bg-blue-100">
        <Icon className="w-4 h-4"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-slate-500 truncate">{desc}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700"/>
    </Link>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    rose: 'bg-rose-50 text-rose-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[accent]}`}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="text-slate-500 text-xs uppercase font-medium">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-3">{value}</div>
    </div>
  );
}

function Mini({ label, value, suffix = '', sub, trend }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
      <div className="text-xs text-blue-200">{label}</div>
      <div className="font-bold text-white mt-1 flex items-baseline gap-1">
        {value}<span className="text-xs opacity-80">{suffix}</span>
        {trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-300 ml-1"/>}
        {trend === 'down' && <TrendingDown className="w-3 h-3 text-rose-300 ml-1"/>}
      </div>
      {sub && <div className="text-xs text-blue-200/70 mt-0.5">{sub}</div>}
    </div>
  );
}
